import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, FlaskConical, SlidersHorizontal, Settings, Package, Rocket, MessageSquare, FileText, Brain, Theater, Wrench, ArrowLeft } from 'lucide-react';
import apiClient from '../api/client';
import { agentStatusMeta } from '../utils/agentStatus';

interface Agent {
  id: string;
  name: string;
  description: string | null;
  domain: string | null;
  status: string;
  llm_config_id: string | null;
}

interface LLMConfig {
  id: string;
  name: string;
  provider: string;
  model: string;
}

export default function AgentOverview() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [llmConfig, setLlmConfig] = useState<LLMConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const loadAgent = useCallback(async () => {
    try {
      const res = await apiClient.get(`/agents/${agentId}`);
      setAgent(res.data);
      // Load LLM config if set
      if (res.data.llm_config_id) {
        const llmRes = await apiClient.get(`/llm-configs/${res.data.llm_config_id}`);
        setLlmConfig(llmRes.data);
      }
    } catch (e) {
      console.error('Failed to load agent:', e);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      if (agentId) void loadAgent();
    };
    tick();
  }, [agentId, loadAgent]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="container">
        <div className="empty">
          <h3>Agent 不存在</h3>
          <button className="primary" onClick={() => navigate('/')}>返回列表</button>
        </div>
      </div>
    );
  }

  // SDD §IV.3 新状态徽标元数据；旧状态（active/training/archived）走下方既有渲染
  const statusMeta = agentStatusMeta(agent.status);

  return (
    <div className="container">
      <button className="secondary mb-4" onClick={() => navigate('/')}>
        <ArrowLeft size={16} /> 返回列表
      </button>

      <div className="card mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 style={{ margin: 0 }}>{agent.name}</h1>
            <p>{agent.description || '暂无描述'}</p>
            {agent.domain && <span className="badge info">{agent.domain}</span>}
          </div>
          {statusMeta ? (
            <span className={`badge ${statusMeta.badgeClass}`}>{statusMeta.label}</span>
          ) : (
            <span className={`badge ${agent.status === 'active' ? 'success' : 'warning'}`}>
              {agent.status === 'active' ? '活跃' : agent.status}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-2 gap-4 mb-4">
        {/* 示教中心 */}
        <div className="card card-clickable" onClick={() => navigate(`/agents/${agentId}/teaching`)}>
          <h3><BookOpen size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />示教中心</h3>
          <p>通过对话、文档等方式教 Agent 新能力</p>
        </div>

        {/* 测试验证 */}
        <div className="card card-clickable" onClick={() => navigate(`/agents/${agentId}/testing`)}>
          <h3><FlaskConical size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />测试验证</h3>
          <p>测试 Agent 对话效果，上传测试用例</p>
        </div>

        {/* 配置中心 */}
        <div className="card card-clickable" onClick={() => navigate(`/agents/${agentId}/config-center`)}>
          <h3><SlidersHorizontal size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />配置中心</h3>
          <p>管理 Agent 的人设、技能、工作流、记忆、工具、知识库</p>
        </div>

        {/* LLM 配置 */}
        <div className="card card-clickable" onClick={() => navigate(`/agents/${agentId}/config`)}>
          <h3><Settings size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />LLM 配置</h3>
          <p>
            {llmConfig
              ? `${llmConfig.name} (${llmConfig.provider}/${llmConfig.model})`
              : '尚未配置 LLM'
            }
          </p>
        </div>

        {/* Essence 导出 */}
        <div className="card card-clickable" onClick={() => navigate(`/agents/${agentId}/essences`)}>
          <h3><Package size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />Essence 导出</h3>
          <p>导出为可移植的能力包</p>
        </div>

        {/* 部署管理 */}
        <div className="card card-clickable" onClick={() => navigate(`/agents/${agentId}/deployments`)}>
          <h3><Rocket size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />部署管理</h3>
          <p>创建运行时、管理容器、查看日志（支持 Generic / CT v4 / Hermes 等框架）</p>
        </div>
      </div>

      {/* 快速入口 */}
      <div className="card">
        <h3>快速入口</h3>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          <button className="secondary" onClick={() => navigate(`/agents/${agentId}/teaching/dialogue`)}>
            <MessageSquare size={14} /> 对话示教
          </button>
          <button className="secondary" onClick={() => navigate(`/agents/${agentId}/teaching/documents`)}>
            <FileText size={14} /> 文档示教
          </button>
          <button className="secondary" onClick={() => navigate(`/agents/${agentId}/teaching/knowledge`)}>
            <Brain size={14} /> 知识库
          </button>
          <button className="secondary" onClick={() => navigate(`/agents/${agentId}/teaching/persona`)}>
            <Theater size={14} /> 人设配置
          </button>
          <button className="secondary" onClick={() => navigate(`/agents/${agentId}/teaching/tools`)}>
            <Wrench size={14} /> 工具配置
          </button>
          <button className="primary" onClick={() => navigate(`/agents/${agentId}/deployments`)}>
            <Rocket size={14} /> 部署管理
          </button>
        </div>
      </div>
    </div>
  );
}